import { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { InternalTrainingShare } from '@/hooks/useInternalTrainingShares';

interface InternalTrainingShareDialogState {
  isOpen: boolean;
  editingShare: InternalTrainingShare | null;
  onSuccessCallback?: () => void;
}

interface InternalTrainingShareDialogContextType {
  state: InternalTrainingShareDialogState;
  openCreateDialog: (onSuccess?: () => void) => void;
  openEditDialog: (share: InternalTrainingShare, onSuccess?: () => void) => void;
  closeDialog: () => void;
}

const InternalTrainingShareDialogContext = createContext<InternalTrainingShareDialogContextType | undefined>(undefined);

export function InternalTrainingShareDialogProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<InternalTrainingShareDialogState>({
    isOpen: false,
    editingShare: null,
    onSuccessCallback: undefined,
  });

  const openCreateDialog = useCallback((onSuccess?: () => void) => {
    setState({
      isOpen: true,
      editingShare: null,
      onSuccessCallback: onSuccess,
    });
  }, []);

  const openEditDialog = useCallback((share: InternalTrainingShare, onSuccess?: () => void) => {
    setState({
      isOpen: true,
      editingShare: share,
      onSuccessCallback: onSuccess,
    });
  }, []);

  const closeDialog = useCallback(() => {
    setState({
      isOpen: false,
      editingShare: null,
      onSuccessCallback: undefined,
    });
  }, []);

  return (
    <InternalTrainingShareDialogContext.Provider value={{ state, openCreateDialog, openEditDialog, closeDialog }}>
      {children}
    </InternalTrainingShareDialogContext.Provider>
  );
}

export function useInternalTrainingShareDialog() {
  const context = useContext(InternalTrainingShareDialogContext);
  if (!context) {
    throw new Error('useInternalTrainingShareDialog must be used within a InternalTrainingShareDialogProvider');
  }
  return context;
}
